import { useEffect, useState } from 'react';
import { useLang } from '../../../i18n';
import { useToast } from '../../../components/Toast';
import { get, patch } from '../../../services/api';
import Card from '../../../components/Card';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import Skeleton from '../../../components/Skeleton';

const CHANNELS = ['email', 'telegram', 'sms', 'push'] as const;
type Channel = typeof CHANNELS[number];

interface EventRow {
  key: string;
  label: string;
  values: Record<Channel, boolean>;
}

const DEFAULT_EVENTS: { key: string; label: string }[] = [
  { key: 'order_status', label: 'Order status changes' },
  { key: 'order_message', label: 'New order message' },
  { key: 'invoice', label: 'Invoice events' },
  { key: 'support', label: 'Support replies' },
];

export default function ProfileNotifications() {
  const { t } = useLang();
  const toast = useToast();
  const [rows, setRows] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get<Record<string, Record<Channel, boolean>>>('/me/notification-preferences/');
        if (cancelled) return;
        const next = DEFAULT_EVENTS.map((e) => ({
          ...e,
          values: { email: false, telegram: false, sms: false, push: false, ...(res?.[e.key] || {}) },
        }));
        setRows(next);
      } catch {
        if (!cancelled) setRows(DEFAULT_EVENTS.map((e) => ({ ...e, values: { email: false, telegram: false, sms: false, push: false } })));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (key: string, channel: Channel, v: boolean) => {
    setRows((arr) => arr.map((r) => r.key === key ? { ...r, values: { ...r.values, [channel]: v } } : r));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: Record<string, Record<Channel, boolean>> = {};
      rows.forEach((r) => { payload[r.key] = r.values; });
      await patch('/me/notification-preferences/', payload);
      toast.success(t('profile.saved'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton height={200} />;

  return (
    <Card padding="md">
      <table className="profile-notif__table">
        <thead>
          <tr>
            <th className="profile-notif__th">{t('profile.notifications.event')}</th>
            {CHANNELS.map((c) => <th key={c} className="profile-notif__th">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="profile-notif__row">
              <td className="profile-notif__td">{r.label}</td>
              {CHANNELS.map((c) => (
                <td key={c} className="profile-notif__td">
                  <Checkbox checked={r.values[c]} onChange={(e) => toggle(r.key, c, e.target.checked)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 'var(--s-4)' }}>
        <Button variant="primary" size="md" loading={saving} onClick={save}>{t('profile.notifications.save')}</Button>
      </div>
    </Card>
  );
}
